import classNames from 'classnames';
import { compose, map } from 'lodash/fp';
import { useEffect } from 'react';
import { useAsync } from 'react-async-hook';
import { useHistory } from 'react-router';
import HorizontalRadio from '../../../components/HorizontalRadio';
import { mapProps } from '../../../hoc/recompose';
import { withData } from '../../../hoc/withData';
import { useAppContext } from '../../../hooks/useAppContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { isAnyAdmin, isAnyCycleAdmin } from '../../../services/User';
import { PureNavLink } from './PureNavLink';
import { adminLinks, employeeLinks } from './links';
import { isMatching, toView, useNavigation } from './service';
import styles from './style.m.less';

const withStatusLabel = C => props => {
    const { user } = useAppContext();
    const { getStatusLabel } = props;
    const { result: statusLabel } = useAsync(() => getStatusLabel?.(user) || Promise.resolve(), [getStatusLabel, user]);
    return <C {...props} statusLabel={statusLabel} />;
};

const withSubscriber = C =>
    compose(
        withData({
            observe: {
                notification: props => props.notifier.observe(),
            },
            pendingComponent: C, // While pending, show component without the notification
            errorComponent: C, // If error happens for some reason, pretend that all is fine
        }),
        mapProps(props => ({
            ...props,
            notification: props.notifier.toNotification(props.notification),
        })),
        withStatusLabel
    )(C);

function BaseNavigation({ links, isAdminNav }) {
    let i = 0;
    return (
        <nav role="navigation">
            {map(
                group => (
                    <Group key={i++} className={classNames(group.className, { [styles.isAdmin]: isAdminNav })}>
                        {map(link => {
                            const NavLink = link.notifier ? withSubscriber(PureNavLink) : withStatusLabel(PureNavLink);
                            return <NavLink {...link} key={link.route + JSON.stringify(link.routeSearchParams)} />;
                        }, group.links)}
                    </Group>
                ),
                links
            )}
        </nav>
    );
}

function Group({ children, className }) {
    return (
        <ul role="presentation" className={classNames(styles.group, className)}>
            {children}
        </ul>
    );
}

const adminValue = 'admin';
const employeeValue = 'employee';
const radioOptions = [
    {
        label: 'Employee',
        value: employeeValue,
    },
    {
        label: 'Admin',
        value: adminValue,
    },
];

function getCurrentNav(previousNav, showAdminNav, currentLocation, highlightComingSoon) {
    if (!showAdminNav) {
        return employeeValue;
    } else if (previousNav === employeeValue || previousNav === adminValue) {
        return previousNav;
    } else if (
        adminLinks(highlightComingSoon).some(group => group.links.some(link => isMatching(currentLocation, link)))
    ) {
        return adminValue;
    } else {
        return employeeValue;
    }
}

export function NewNavigation({ currentLocation, appContext, highlightComingSoon }) {
    const history = useHistory();
    const [previousNav, setPreviousNav] = useLocalStorage('nav-history', null);
    const showAdminNav = isAnyAdmin(appContext.user) || isAnyCycleAdmin(appContext.user);
    const currentNav = getCurrentNav(previousNav, showAdminNav, currentLocation, highlightComingSoon);
    const [selectedNav, setSelectedNav] = useNavigation(currentNav);

    useEffect(() => {
        if (history) {
            return history.listen((_location, action) => {
                if (action === 'POP') {
                    setPreviousNav(null);
                } else {
                    setPreviousNav(currentNav);
                }
            });
        }
    }, [history, setPreviousNav, currentNav]);

    const handleChangeSelectedNav = navValue => {
        setPreviousNav(navValue);
        setSelectedNav(navValue);
    };

    return (
        <>
            {showAdminNav && (
                <HorizontalRadio
                    small={false}
                    name="navigation"
                    className={styles.navSelect}
                    options={radioOptions}
                    value={selectedNav}
                    onChange={handleChangeSelectedNav}
                />
            )}
            <div className={styles.navContainer}>
                <div
                    className={classNames(styles.nav, styles.navEmployee, {
                        [styles.show]: selectedNav === employeeValue,
                    })}
                >
                    <BaseNavigation links={toView(employeeLinks, appContext.user, currentLocation)} />
                </div>
                <div
                    className={classNames(styles.nav, styles.navAdmin, {
                        [styles.show]: selectedNav === adminValue,
                    })}
                >
                    <BaseNavigation
                        links={toView(adminLinks(highlightComingSoon), appContext.user, currentLocation)}
                        isAdminNav
                    />
                </div>
            </div>
        </>
    );
}
